import * as React from 'react'

import { Box, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Delete } from '@factorialco/f0-react/icons/app'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import DeleteBulkModal from 'modules/shift_management/components/ShiftManagement/DeleteShiftModals/DeleteBulkModal'
import DeleteSelectedModal from 'modules/shift_management/components/ShiftManagement/DeleteShiftModals/DeleteSelectedModal'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionState } from 'modules/shift_management/hooks/useSelection'
import shiftManagementCounts from 'modules/shift_management/lib/shiftManagementCounts'
import type { RestDay } from 'modules/shift_management/lib/types'
import type Shift from 'modules/shift_management/store/internal/models/Shift'

interface Props {
  disabled?: boolean
}

const DeleteButton = ({ disabled }: Props) => {
  const [{ view }] = useSchedule()
  const {
    shifts: selectedShifts,
    restDays: selectedRestDays,
    daysByRow,
    kind,
    isResolvingGridSelection,
  } = useSelectionState()

  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState<boolean>(false)
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = React.useState<boolean>(false)
  const [isTooltipOpen, toggleTooltip] = useToggle(false)

  const shifts = Object.values(selectedShifts)
  const restDays = Object.values(selectedRestDays)

  const onDeleteAction = () => {
    const { shiftsCount, restDaysCount, employeeCount } = shiftManagementCounts(shifts, restDays)
    const shiftsIds = shifts.map((s: Shift) => s.id)
    const restDayIds = restDays.map((r: RestDay) => r.id)
    const selectedEmployeeIds = Object.keys(daysByRow)

    track(trackingEvents.shiftManagement.deleteShiftStart, {
      shifts_ids: shiftsIds,
      rest_day_ids: restDayIds,
      number_of_impacted_employees: Math.max(employeeCount, selectedEmployeeIds.length),
      number_of_impacted_shifts: shiftsCount,
      number_of_impacted_rest_days: restDaysCount,
      view: view.viewType,
      view_type_by: view.groupType,
      from: 'top_bar_button',
    })
    if (kind === 'cells' || kind === 'event') return setIsDeleteModalOpen(true)

    return setIsBulkDeleteModalOpen(true)
  }

  return (
    <>
      <Box onMouseEnter={toggleTooltip(!disabled)} onMouseLeave={toggleTooltip(false)}>
        <Tooltip.Alt
          open={isTooltipOpen}
          attachment="top center"
          label={i18n.t('shift_management.actions.delete')}
        >
          <F0Button
            hideLabel
            icon={Delete}
            onClick={onDeleteAction}
            size="md"
            variant="outline"
            disabled={disabled || isResolvingGridSelection}
          />
        </Tooltip.Alt>
      </Box>

      {isBulkDeleteModalOpen && <DeleteBulkModal onClose={() => setIsBulkDeleteModalOpen(false)} />}
      {isDeleteModalOpen && (
        <DeleteSelectedModal
          shifts={shifts}
          restDays={restDays}
          onClose={() => setIsDeleteModalOpen(false)}
        />
      )}
    </>
  )
}

export default DeleteButton
